import type {
  ResolveRoundAction,
  SkipFlickAction,
  StoneFlickingConfig,
  StoneFlickingState,
  SubmitFlickAction,
  SurrenderAction,
} from "@cloudnest/augmented-stone-flicking-engine";

export type AsfResolveRoundReplayAction = ResolveRoundAction & {
  readonly randomSeed?: number;
};

export type AsfReplayAction =
  | SubmitFlickAction
  | SkipFlickAction
  | AsfResolveRoundReplayAction
  | SurrenderAction;

export type AsfReplayEvent =
  | {
      readonly eventType: "init";
      readonly userId: string;
      readonly payload: {
        readonly state: StoneFlickingState;
        readonly config: StoneFlickingConfig;
      };
    }
  | {
      readonly eventType: "action";
      readonly userId: string;
      readonly payload: AsfReplayAction;
    };

export type AsfReplayEventType = AsfReplayEvent["eventType"];

export interface AsfReplayEventInput {
  readonly eventType: string;
  readonly userId?: string;
  readonly payload?: unknown;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function requireUserId(event: AsfReplayEventInput): string {
  if (typeof event.userId === "string" && event.userId.length > 0) {
    return event.userId;
  }
  throw new Error("ASF replay event is missing userId");
}

export function normalizeAsfReplayEventType(
  eventType: string,
): AsfReplayEventType {
  switch (eventType) {
    case "init":
    case "action":
      return eventType;
    default:
      throw new Error(`Unsupported ASF event type: ${eventType}`);
  }
}

function toAsfReplayAction(payload: unknown): AsfReplayAction {
  if (!isRecord(payload) || typeof payload.type !== "string") {
    throw new Error("ASF replay action is missing type");
  }

  switch (payload.type) {
    case "SUBMIT_FLICK":
    case "SKIP_FLICK":
    case "RESOLVE_ROUND":
    case "SURRENDER":
      return payload as unknown as AsfReplayAction;
    default:
      throw new Error(`Unsupported ASF replay action: ${payload.type}`);
  }
}

export function toAsfReplayEvent(event: AsfReplayEventInput): AsfReplayEvent {
  const eventType = normalizeAsfReplayEventType(event.eventType);
  const userId = requireUserId(event);
  const payload = event.payload ?? {};

  switch (eventType) {
    case "init":
      return {
        eventType,
        userId,
        payload: payload as {
          readonly state: StoneFlickingState;
          readonly config: StoneFlickingConfig;
        },
      };
    case "action":
      return {
        eventType,
        userId,
        payload: toAsfReplayAction(payload),
      };
  }
}
